import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { directAuth } from '../../services/directAuth';
import { graphqlApi } from '../../services/graphql';
import { useAuthStore } from '../../store/authStore';
import { retrieveOAuthState, clearOAuthState } from '../../utils/oauth';
import {
  getLastVisitedPath,
  clearLastVisitedPath,
  getPostAuthRedirectPath,
} from '../../utils/authRedirect';

interface OAuthCallbackHandlerProps {
  onSuccess?: (data: any) => void;
  onError?: (error: Error) => void;
}

// Parse code and state from URL immediately (before React renders)
function getOAuthParamsFromUrl(): { code: string | null; state: string | null; error: string | null; errorDescription: string | null } {
  const params = new URLSearchParams(window.location.search);
  return {
    code: params.get('code'),
    state: params.get('state'),
    error: params.get('error'),
    errorDescription: params.get('error_description'),
  };
}

export const OAuthCallbackHandler: React.FC<OAuthCallbackHandlerProps> = ({
  onSuccess,
  onError,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const [error, setError] = useState<string | null>(null);
  const setAuthenticatedUser = useAuthStore((state) => state.setAuthenticatedUser);
  const setToken = (token: string | null) => useAuthStore.setState({ token });
  const processedRef = useRef(false);

  useEffect(() => {
    // Prevent double execution (React StrictMode)
    if (processedRef.current) return;
    processedRef.current = true;

    const handleCallback = async () => {
      try {
        // Read params directly from window.location (before Amplify can intercept)
        const { code, state, error: errorParam, errorDescription } = getOAuthParamsFromUrl();

        console.log('🔄 OAuth callback params:', { code: code?.substring(0, 10), state: state?.substring(0, 10), errorParam });

        // Handle OAuth provider errors
        if (errorParam) {
          const errorMessage = errorDescription || errorParam;
          console.error('OAuth provider error:', errorMessage);
          setError(errorMessage);
          
          if (onError) {
            onError(new Error(errorMessage));
          }
          
          setTimeout(() => navigate('/login', { replace: true }), 3000);
          return;
        }

        if (!code) {
          console.error('Missing authorization code');
          setError('Missing authorization code. Please try again.');
          
          if (onError) {
            onError(new Error('Missing authorization code'));
          }
          
          setTimeout(() => navigate('/login', { replace: true }), 3000);
          return;
        }

        console.log('🔄 Processing OAuth callback...');

        // social login (connect) flow — link the provider to the current
        // account instead of signing in. Detected via the stored OAuth state.
        const oauthState = retrieveOAuthState();
        if (oauthState?.connect && oauthState.provider) {
          const currentUser = useAuthStore.getState().user;
          const redirectBack = oauthState.redirectPath || '/my-profile/info';
          clearOAuthState();
          if (!currentUser?.userId) {
            navigate(`${redirectBack}?connect=${oauthState.provider}&status=error`, { replace: true });
            return;
          }
          const linkRes = await graphqlApi.linkSocialAccount(
            currentUser.userId,
            oauthState.provider,
            code,
            `${window.location.origin}/oauth/callback`,
          );
          const status = linkRes.success
            ? 'success'
            : linkRes.alreadyLinkedToOther
              ? 'conflict'
              : 'error';
          navigate(`${redirectBack}?connect=${oauthState.provider}&status=${status}`, { replace: true });
          return;
        }

        // Exchange code for tokens and authenticate
        const result = await directAuth.handleOAuthCallback(code, state || '');

        if (result.success && result.data) {
          console.log(`✅ OAuth authentication successful`);

          const user = result.data.user;
          let isNewUser = !user.userType;

          // Extra guard: if `userType` is missing but the user already exists in our users table,
          // treat them as an existing user (sign-in), not a new one. Covers the case where an
          // existing OAuth user clicks "Sign up" again — Cognito returns them, but oauthState
          // might not surface a userType, and we don't want to drop them into /complete-registration.
          if (isNewUser && user.userId) {
            try {
              const existing = await graphqlApi.getUser(user.userId);
              if (existing.success && existing.data && existing.data.userType) {
                user.userType = existing.data.userType;
                user.hostId = existing.data.hostId ?? user.hostId;
                user.influencerId = existing.data.influencerId ?? user.influencerId;
                isNewUser = false;
                console.log('ℹ️ Existing user detected; skipping /complete-registration');
              }
            } catch (lookupErr) {
              console.warn('⚠️ User lookup failed, falling through to new-user flow:', lookupErr);
            }
          }

          if (isNewUser) {
            // Redirect to complete registration page to pick userType
            navigate('/complete-registration', {
              replace: true,
              state: {
                userId: user.userId,
                email: user.email,
                // Naver / Kakao sometimes return no usable email —
                // backend synthesizes `<provider>_<id>@<provider>.local`
                // and sets this flag, which tells the complete-registration
                // form to clear the field and require the user to type a
                // real one instead of showing the placeholder as read-only.
                emailSynthetic: (user as any).emailSynthetic === true,
                name: user.name,
                profileImage: user.profileImage,
                oauthProvider: user.oauthProvider,
                tokens: result.data.tokens,
                naverId: (user as any).naverId,
                kakaoId: (user as any).kakaoId,
              },
            });
            return;
          }

          if (user) {
            setAuthenticatedUser(user);
            // Store the Cognito token if returned (Naver/Kakao OAuth flow)
            if (result.data.tokens?.idToken) {
              setToken(result.data.tokens.idToken);
            }

            // Reconcile the canonical email/name from the users table. OAuth
            // logins (esp. Kakao) can carry a synthetic
            // `<provider>_<id>@<provider>.local` address; without this the
            // wrong email sits in local storage until a manual refresh runs
            // checkAuthState. Done AFTER setAuthenticatedUser/setToken so the
            // authenticated getUser call has a token to use.
            try {
              const canonical = await graphqlApi.getUser(user.userId);
              const cu = (canonical as any)?.data?.getUser || (canonical as any)?.data;
              if (canonical?.success && cu?.email && cu.email !== user.email) {
                useAuthStore.getState().updateUser({
                  ...user,
                  email: cu.email,
                  name: cu.name || user.name,
                });
              }
            } catch (reconcileErr) {
              console.warn('⚠️ Canonical email reconcile failed:', reconcileErr);
            }
          }

          if (onSuccess) {
            onSuccess(result.data);
          }

          // Prefer the last non-auth page the user was on before starting
          // OAuth (populated by <LocationTracker> in App.tsx), then any
          // stored redirect intent, then the backend's suggested path,
          // then /dashboard. This covers the "clicked 로그인 from / then
          // signed in with Google" flow — the backend doesn't know what
          // the user was viewing, but the tracker does.
          const last = getLastVisitedPath();
          const storedIntent = getPostAuthRedirectPath('');
          const redirectPath = last || storedIntent || result.data.redirectPath || '/dashboard';
          clearLastVisitedPath();
          console.log('🔀 Redirecting to:', redirectPath, {
            source: last ? 'tracker' : storedIntent ? 'intent' : result.data.redirectPath ? 'backend' : 'default',
          });
          navigate(redirectPath, { replace: true });
        } else {
          const errorMsg = result.error || 'OAuth authentication failed';
          console.error('❌ OAuth authentication failed:', errorMsg);
          setError(errorMsg);

          if (onError) {
            onError(new Error(errorMsg));
          }

          setTimeout(() => navigate('/login', { replace: true }), 3000);
        }
      } catch (err) {
        console.error('❌ OAuth callback handling error:', err);
        setError('An unexpected error occurred. Please try again.');

        if (onError) {
          onError(err as Error);
        }

        setTimeout(() => navigate('/login', { replace: true }), 3000);
      }
    };

    handleCallback();
  }, [navigate, onSuccess, onError, setAuthenticatedUser]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        {error ? (
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <svg
                className="h-6 w-6 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t('oauth.authenticationFailed')}
            </h3>
            <p className="text-sm text-gray-600 mb-4">{error}</p>
            <p className="text-xs text-gray-500">
              {t('oauth.redirectingToLogin')}
            </p>
          </div>
        ) : (
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-primary-100 mb-4">
              <svg
                className="animate-spin h-6 w-6 text-primary-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t('oauth.completingAuthentication')}
            </h3>
            <p className="text-sm text-gray-600">
              {t('oauth.pleaseWait')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
